<template>
  <div class="news-management">
    <div class="container-fluid">
      <header class="page-header mb-4">
        <div class="row align-items-center">
          <div class="col-12 col-md-6 mb-3 mb-md-0">
            <h2 class="page-title mb-0">
              <a href="#" @click.prevent="goToDashboard" class="breadcrumb-link">Dashboard</a>
              <span class="breadcrumb-separator">&gt;</span>
              <span class="breadcrumb-current">News Management</span>
            </h2>
          </div>

          <!-- Right column: ensure it lives in the second column so on md+ it's on the same row -->
          <div class="col-12 col-md-6">
            <div class="header-actions d-flex gap-2 justify-content-md-end align-items-center">
              <!-- Search input: rounded, white background with right-side search icon.
                    Keep event handlers: onSearch (button/icon click) and onSearchKey (Enter key). -->
              <el-input
                v-model="searchQuery"
                class="search-input-v2"
                placeholder="Search news..."
                clearable
                @keyup="onSearchKey"
              >
                <template #suffix>
                  <el-icon class="search-icon" @click="onSearch">
                    <Search />
                  </el-icon>
                </template>
              </el-input>

              <button class="btn btn-primary d-flex align-items-center" @click="openAddNews">
                <el-icon><Plus /></el-icon>&nbsp;<span class="d-none d-sm-inline">Add News</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div v-if="loading" class="loading-state text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading news...</p>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>

      <div v-else class="row g-3 mb-4">
        <div v-for="newsItem in paginatedNews" :key="newsItem.newsId" class="col-12 col-md-6">
          <div class="card news-card h-100">
            <div class="card-body d-flex flex-column">
              <!-- Top row: publish date (left) and Published/Disabled badge (right) -->
              <div class="d-flex justify-content-between align-items-center mb-2 news-top-row">
                <div class="news-meta d-flex align-items-center gap-2 mb-3 small text-muted">
                  <el-icon><Calendar /></el-icon>
                  <span>{{ convertFromUTC(newsItem.publishDate) }}</span>
                </div>

                <div class="news-badge-wrap" style="margin-top: -22px;">
                  <span :class="['badge', newsItem.disabled ? 'bg-secondary' : 'bg-success']">
                    {{ newsItem.disabled ? 'Disabled' : 'Published' }}
                  </span>
                </div>
              </div>

              <!-- Title below top row -->
              <h5 class="news-title mb-2">{{ newsItem.mainTitle }}</h5>

              <div class="d-flex gap-2 mb-3 align-items-start">
                <img 
                  v-if="hasNewsImage(newsItem)" 
                  :src="getNewsImageUrl(newsItem)" 
                  alt="News thumbnail"
                  class="news-thumbnail"
                  @error="handleImageError"                  
                />
                <div 
                  :class="['flex-grow-1', hasNewsImage(newsItem) ? 'd-flex align-items-center' : '']" 
                  class="subtitle-container"
                >
                  <p class="news-subtitle text-muted small mb-0">{{ newsItem.subTitle }}</p>
                </div>
              </div>

              <div class="mt-auto">
                <div class="btn-group w-100" role="group">
                  <button class="btn btn-outline-info btn-sm" @click="previewNews(newsItem)" title="Preview">
                    <el-icon><View /></el-icon>
                  </button>
                  <button class="btn btn-outline-primary btn-sm" @click="editNews(newsItem)" title="Edit">
                    <el-icon><Edit /></el-icon>
                  </button>
                  <button 
                    class="btn btn-outline-secondary btn-sm" 
                    @click="toggleNewsStatus(newsItem)" 
                    :title="newsItem.disabled ? 'Enable' : 'Disable'"
                  >
                    <el-icon v-if="!newsItem.disabled"><Hide /></el-icon>
                    <el-icon v-else><Open /></el-icon>
                  </button>
                  <button class="btn btn-outline-danger btn-sm" @click="deleteNews(newsItem)" title="Delete">
                    <el-icon><Delete /></el-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <nav v-if="!loading && totalPages > 0" aria-label="News pagination">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="currentPage--" :disabled="currentPage === 1">
              <div class="d-flex justify-content-start">
              <div style="padding-right: 1px; margin-top: 3px;"><el-icon><ArrowLeft/></el-icon></div>
              <div><span class="d-none d-sm-inline">Previous</span></div>
              </div>
            </button>
          </li>
          <li class="page-item disabled">
            <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="currentPage++" :disabled="currentPage === totalPages">
              <div class="d-flex justify-content-start">
              <div><span class="d-none d-sm-inline">Next</span></div>
              <div style="padding-left: 1px; margin-top: 3px;"><el-icon><ArrowRight /></el-icon></div >
              </div>
            </button>
          </li>
        </ul>
      </nav>

      <div v-if="!loading && newsCount === 0 && !error" class="alert alert-info text-center" role="alert">
        No news found
      </div>

      <!-- Add/Edit News Dialog -->
      <el-dialog
        v-model="showAddNewsDialog"
        :title="isEditMode ? 'Edit News' : 'Add New News'"
        width="450px"
        :close-on-click-modal="false"
        center
        :before-close="handleDialogBeforeClose"
      >
        <el-form :model="addNewsForm" :rules="addNewsRules" ref="addNewsFormRef" label-width="0px">
          <el-form-item prop="mainTitle">
            <el-input
              v-model="addNewsForm.mainTitle"
              placeholder="Enter main title"
              clearable
            />
          </el-form-item>
          <el-form-item prop="subTitle">
            <el-input
              v-model="addNewsForm.subTitle"
              placeholder="Enter sub title"
              clearable
            />
          </el-form-item>
          <el-form-item prop="image" class="image-upload-item centered-form-item">
            <el-collapse v-model="imageCollapseActive" class="image-collapse">
              <el-collapse-item name="1">
                <template #title>
                  <div class="collapse-title">
                    <el-icon v-if="imagePreview"><PictureFilled /></el-icon>
                    <el-icon v-else><Picture /></el-icon>
                    <span>Upload Image</span>
                  </div>
                </template>
                <el-upload
                  class="news-image-uploader"
                  :auto-upload="false"
                  :show-file-list="false"
                  :on-change="handleImageChange"
                  :before-upload="beforeImageUpload"
                  accept="image/png"
                  :disabled="!!imagePreview"
                >
                  <div v-if="imagePreview" class="image-preview-container" @click.stop>
                    <el-image 
                      :src="imagePreview" 
                      fit="cover"
                      class="preview-image"
                      :preview-src-list="[imagePreview]"
                      :z-index="3000"
                    >
                      <template #error>
                        <div class="image-error">
                          <el-icon><Picture /></el-icon>
                        </div>
                      </template>
                    </el-image>
                    <div class="image-actions">
                      <el-button 
                        size="small" 
                        type="danger" 
                        @click.stop="removeImage"
                        :icon="Delete"
                      >
                        Remove
                      </el-button>
                    </div>
                  </div>
                  <div v-else class="upload-placeholder">
                    <el-icon class="upload-icon"><Plus /></el-icon>                
                    <div class="upload-hint">PNG (Max 3MB)</div>
                  </div>
                </el-upload>
              </el-collapse-item>
            </el-collapse>
          </el-form-item>
          <el-form-item prop="content">
            <el-input
              v-model="addNewsForm.content"
              type="textarea"
              :rows="6"
              placeholder="Enter news content"
              resize="vertical"
            />
          </el-form-item>
          <el-form-item label="Publish Date" prop="publishDate" class="centered-form-item">
            <el-date-picker
              v-model="addNewsForm.publishDate"
              type="date"
              placeholder="Select publish date"
              style="width: 100%"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD HH:mm:ss"
              :disabled-date="disabledDate"
            />
          </el-form-item>
          <el-form-item label="Status" prop="disabled" class="centered-form-item">
            <el-switch 
              v-model="newsPublished"
              active-text="Published" 
              inactive-text="Disabled"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <div class="dialog-footer">
            <!--<el-button @click="showAddNewsDialog = false">Cancel</el-button>-->
            <el-button @click="handleCancelDialog">Cancel</el-button>
            <el-button type="primary" @click="handleSubmitNews" :loading="addingNews">
              {{ isEditMode ? 'Update' : 'Add News' }}
            </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import apiClient from '@/apiClient';
import { ElMessage } from 'element-plus';
import { MONTHS } from '@/constants';

const router = useRouter();
const route = useRoute();
const { proxy } = getCurrentInstance();

// state
const newsList = ref([]);
const newsCount = ref(0);
const loading = ref(true);
const error = ref('');
const searchQuery = ref((route.query.search || '').toString());
const currentPage = ref(1);
const pageSize = ref(8);
const showAddNewsDialog = ref(false);
const addingNews = ref(false);
const addNewsFormRef = ref(null);
const newsImagesLoaded = ref(new Set());
const newsImagesFailed = ref(new Set());
const deletedImageIds = ref(new Set()); 
const imageVersionMap = ref(new Map()); // cache-busting versions (per-newsId)
const imagePreview = ref('');
const imageFile = ref(null);
const imageCollapseActive = ref([]);
const isEditMode = ref(false);
const editingNewsId = ref(null);
const hasExistingImage = ref(false);
const imageChanged = ref(false); 

// user clicked "Remove" inside dialog but hasn't confirmed with Update yet
const removedImagePending = ref(false);
// whether the news had an image when the edit dialog opened
const imageHadAtOpen = ref(false);
// cached original image URL for restoration if user cancels
const originalImageUrl = ref('');


// Add news form
const addNewsForm = ref({
  mainTitle: '',
  subTitle: '',
  content: '',
  publishDate: '',
  disabled: false
});

// Computed property for switch state (inverted from disabled)
const newsPublished = computed({
  get: () => !addNewsForm.value.disabled,
  set: (val) => {
    addNewsForm.value.disabled = !val;
  }
});

// Get today's date in YYYY-MM-DD format
function getTodayDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

// Convert UTC to local date for date picker
function convertUTCToLocalDate(utcString) {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day} 00:00:00`;
}

// Convert local date to UTC ISO string
function convertToUTC(dateString) {
  if (!dateString) return null;
  
  // Parse the date string (YYYY-MM-DD HH:mm:ss)
  const localDate = new Date(dateString);
  
  // Return ISO 8601 UTC format: "2025-11-21T00:00:00.000Z"
  return localDate.toISOString();
}

// Convert UTC ISO string to local date string for display
function convertFromUTC(utcString) {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  const year = date.getFullYear();    
  
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${year}`;  
}

// Disable future dates
function disabledDate(time) {
  // Disable dates after today
  return time.getTime() > Date.now();
}

// Add news validation rules
const addNewsRules = {
  mainTitle: [
    { required: true, message: 'Please enter main title', trigger: 'blur' },
    { min: 3, message: 'Main title must be at least 3 characters', trigger: 'blur' }
  ],
  subTitle: [
    { required: true, message: 'Please enter sub title', trigger: 'blur' }
  ],
  content: [
    { required: true, message: 'Please enter content', trigger: 'blur' },
    { min: 10, message: 'Content must be at least 10 characters', trigger: 'blur' }
  ],
  publishDate: [
    { required: true, message: 'Please select publish date', trigger: 'change' }
  ]
};

const totalPages = computed(() => Math.ceil(newsCount.value / pageSize.value));

const paginatedNews = computed(() => {
  return newsList.value;
});

// watch for page changes
watch(currentPage, async () => {
  await loadNews();  
});

// methods
function goToDashboard() {
  router.push({ name: 'AdminDashboard' }).catch(() => {});
}

// Check if news has thumbnail image
function hasNewsImage(newsItem) {
  
  if (!newsItem || !newsItem.newsId) {
    return false;
  }

  if(newsItem.hasImage === false) {
    return false;
  }

  if (deletedImageIds.value.has(newsItem.newsId)) {
    return false;
  }
  // If image already failed to load, don't show
  if (newsImagesFailed.value.has(newsItem.newsId)) {
    return false;
  }
  // If image loaded successfully, show
  if (newsImagesLoaded.value.has(newsItem.newsId)) {
    return true;
  }
  // Default: try to load the image
  return true;
}

// Get news image URL (use version token if available to bust caches)
function getNewsImageUrl(newsItem) {
  if(newsItem.hasImage === false) {
    return null;
  }
  // English comment: always include a version token and a tiny random suffix to force a network fetch.
  // The token is bumped when we commit deletion/upload so the browser will request fresh resource.
  let ver = imageVersionMap.value.get(newsItem.newsId);
  if (!ver) {
    ver = Date.now();
    imageVersionMap.value.set(newsItem.newsId, ver);
  }
  // add a short random param to bypass any intermediary caches on page refresh
  return `/news/${newsItem.newsId}.png?v=${ver}&_=${Math.floor(Math.random() * 1000000)}`;
}

// Handle image load error
function handleImageError(event) {
  const imgSrc = event.target.src;
  const newsId = imgSrc.match(/\/news\/(\d+)\.png/)?.[1];
  if (newsId) {
    newsImagesFailed.value.add(parseInt(newsId));
    // Force hide the broken img element to avoid stale display
    event.target.style.display = 'none';
  }
}


// Add search handler: sets to first page and reloads list
// English comment: trigger search by button click or Enter key
async function onSearch() {
  currentPage.value = 1;
  // English comment: ensure the route query is updated first so loadNews reads the latest search.
  const trimmed = (searchQuery.value || '').trim();
  await router.replace({ query: { ...route.query, search: trimmed } }).catch(()=>{});
  // ensure reactivity finishes (route updates) before calling loadNews
  await nextTick();
  await loadNews();
}

// Add handler that detects Enter key for the input component
function onSearchKey(e) {
  // English comment: only trigger search when the Enter key is pressed
  if (!e) return;
  if (e.key === 'Enter') {
    onSearch();
  }
}

// loadNews reads the search value from route.query (authoritative) and falls back to local
async function loadNews() {
  loading.value = true;
  error.value = '';
  try {
    // English comment: prefer route.query.search (persisted) so dialog operations won't lose it
    const searchParam = ((route.query.search !== undefined) ? route.query.search : searchQuery.value) || '';
    const response = await apiClient.post('/Main/getAdminPaginatedNewsList', {
      pageIndex: currentPage.value,
      pageSize: pageSize.value,
      search: (searchParam || '').toString().trim()
    });
    if (response && response.data) {
      newsList.value = response.data.news || [];
      newsCount.value = response.data.total || 0;
      newsImagesLoaded.value.clear();
      newsImagesFailed.value.clear();
    }
  } catch (e) {
    error.value = (e?.response?.data?.msg) || 'Failed to load news';    
  } finally {
    loading.value = false;
  }
}

// OPTIONAL: if you programmatically change route.query outside this component, keep local input synced
watch(() => route.query.search, (newVal) => {
  searchQuery.value = (newVal || '').toString();
});

function handleImageChange(file) {
  const isValidType = file.raw.type === 'image/png';
  const isLt3M = file.raw.size / 1024 / 1024 < 3;
  if (!isValidType) {
    ElMessage.error('Image must be PNG format!');
    return false;
  }
  if (!isLt3M) {
    ElMessage.error('Image size cannot exceed 3MB!');
    return false;
  }
  imageFile.value = file.raw;
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreview.value = e.target.result;
    hasExistingImage.value = false;
    imageChanged.value = true;
  };
  reader.readAsDataURL(file.raw);
}

// Before image upload validation
function beforeImageUpload(file) {
  const isValidType = file.type === 'image/png';
  const isLt3M = file.size / 1024 / 1024 < 3;

  if (!isValidType) {
    ElMessage.error('Image must be PNG format!');
    return false;
  }
  if (!isLt3M) {
    ElMessage.error('Image size cannot exceed 3MB!');
    return false;
  }
  return true;
}


function removeImage() {
  // Soft remove: hide preview locally and mark pending deletion.
  // Do NOT update deletedImageIds (permanent deletion) until user clicks Update.
  imagePreview.value = '';
  imageFile.value = null;
  hasExistingImage.value = false;
  imageChanged.value = true;
  removedImagePending.value = true;
}

// Cancel button handler: restore cached original if soft-removed
function handleCancelDialog() {
  if (isEditMode.value && removedImagePending.value && imageHadAtOpen.value && originalImageUrl.value) {
    // restore preview to original image because user cancelled
    imagePreview.value = originalImageUrl.value;
    hasExistingImage.value = true;
    imageChanged.value = false;
    removedImagePending.value = false;
  }
  // close dialog and reset local dialog state
  showAddNewsDialog.value = false;
  resetDialogState();
}

// before-close (dialog X / overlay) behaves same as Cancel
function handleDialogBeforeClose(done) {
  if (isEditMode.value && removedImagePending.value && imageHadAtOpen.value && originalImageUrl.value) {
    imagePreview.value = originalImageUrl.value;
    hasExistingImage.value = true;
    imageChanged.value = false;
    removedImagePending.value = false;
  }
  resetDialogState();
  done();
}


// Upload image to server
async function uploadNewsImage(newsId) {
  if (!imageFile.value) return true;

  const formData = new FormData();
  formData.append('file', imageFile.value);
  formData.append('newsId', newsId);

  try {
    const response = await apiClient.post('/Main/uploadNewsImage', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response && response.data) {
      return true;
    }
    return false;
  } catch (e) {
    ElMessage.error('Failed to upload image');
    return false;
  }
}

// Open dialog for adding new news
function openAddNews() {
  isEditMode.value = false;
  editingNewsId.value = null;
  hasExistingImage.value = false;
  imageChanged.value = false;
  showAddNewsDialog.value = true;

  // Reset form
  addNewsForm.value = {
    mainTitle: '',
    subTitle: '',
    content: '',
    publishDate: getTodayDate(),
    disabled: false
  };
  imagePreview.value = '';
  imageFile.value = null;
  imageCollapseActive.value = [];
  if (addNewsFormRef.value) addNewsFormRef.value.clearValidate();
}

// Open dialog for editing news
async function openEditNews(newsItem) {
  isEditMode.value = true;
  editingNewsId.value = newsItem.newsId;
  showAddNewsDialog.value = true;

  // reset soft-remove flags for this dialog session
  removedImagePending.value = false;
  imageHadAtOpen.value = false;
  originalImageUrl.value = '';
  
  try {
    const response = await apiClient.post('/Main/getNewsDetail', {
      newsId: newsItem.newsId
    });
    if (response && response.data) {
      addNewsForm.value = {
        mainTitle: response.data.newsDetail.mainTitle,
        subTitle: response.data.newsDetail.subTitle,
        content: response.data.newsDetail.contentHtml,
        publishDate: convertUTCToLocalDate(response.data.newsDetail.publishDate),
        disabled: response.data.newsDetail.disabled
      };
    }
  } catch (e) {
    const errorMsg = e?.response?.data?.msg || 'Failed to load news detail';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);      
  }
  // Handle existing image
  if (deletedImageIds.value.has(newsItem.newsId)) {
    imagePreview.value = '';
    hasExistingImage.value = false;
    imageCollapseActive.value = [];
    imageChanged.value = false;
    imageFile.value = null;
    if (addNewsFormRef.value) addNewsFormRef.value.clearValidate();
    return;
  }
  
  // Load existing image if available
  const imageUrl = getNewsImageUrl(newsItem);
  try {
    const img = new Image();
    img.onload = () => {
      imagePreview.value = imageUrl;
      hasExistingImage.value = true;
      imageCollapseActive.value = ['1'];
      imageChanged.value = false;
      removedImagePending.value = false;
      imageHadAtOpen.value = true;
      originalImageUrl.value = imageUrl; // store original for cancel restoration
      newsImagesLoaded.value.add(newsItem.newsId);
      newsImagesFailed.value.delete(newsItem.newsId);
        // mark that an existing image is present so later submit logic
        // can tell the backend this news currently has an image
        imageFile.value = { existing: true };
    };
    img.onerror = () => {
      imagePreview.value = '';
      hasExistingImage.value = false;
      imageCollapseActive.value = [];
      imageChanged.value = false;
      removedImagePending.value = false;
      imageHadAtOpen.value = false;
      originalImageUrl.value = '';
        // ensure imageFile is null when no image is available
        imageFile.value = null;
      newsImagesFailed.value.add(newsItem.newsId);
      newsImagesLoaded.value.delete(newsItem.newsId);
    };
    img.src = imageUrl;
  } catch {
    imagePreview.value = '';
    hasExistingImage.value = false;
    imageCollapseActive.value = [];
    imageChanged.value = false;
      // ensure imageFile is null when an error occurs
      imageFile.value = null;
      newsImagesFailed.value.add(newsItem.newsId);
  }
  
  
  if (addNewsFormRef.value) addNewsFormRef.value.clearValidate();
}

// Handle submit (add or update)
async function handleSubmitNews() {
  if (!addNewsFormRef.value) return;

  await addNewsFormRef.value.validate(async (valid) => {
    if (!valid) return;

    addingNews.value = true;
    try {
      const publishDateUTC = convertToUTC(addNewsForm.value.publishDate);

      const endpoint = isEditMode.value ? '/Main/editNews' : '/Main/addNewNews';
      const payload = {
        ...(isEditMode.value && { newsId: editingNewsId.value }),
        ...(isEditMode.value && { hasImage: imageFile.value !== null }),
        ...(isEditMode.value && { isImageChanged: imageChanged.value }),
        mainTitle: addNewsForm.value.mainTitle,
        subTitle: addNewsForm.value.subTitle,
        contentHtml: addNewsForm.value.content,
        publishDate: publishDateUTC,
        disabled: addNewsForm.value.disabled
      };

      const response = await apiClient.post(endpoint, payload);

      if (response && response.data) {
        const newsId = isEditMode.value ? editingNewsId.value : response.data.newsId;

        if (isEditMode.value && removedImagePending.value && !imageFile.value) {
          deletedImageIds.value.add(newsId);
          newsImagesFailed.value.add(newsId);
          newsImagesLoaded.value.delete(newsId);

          // bump token to bust caches for subsequent loads / reloads
          const token = Date.now();
          imageVersionMap.value.set(newsId, token);
        }

        if (imageChanged.value && imageFile.value && newsId > 0) {
          const uploaded = await uploadNewsImage(newsId);
          if (uploaded) {
            deletedImageIds.value.delete(newsId);
            newsImagesLoaded.value.add(newsId);
            newsImagesFailed.value.delete(newsId);

            // bump token so browser loads the new image, not cached old one
            imageVersionMap.value.set(newsId, Date.now());
            
          }
        }

        // ensure UI probes server state after commit; this prevents stale image appearing after F5
        // English comment: force re-probe for this newsId immediately after commit
        try {
          const probeRes = await fetch(`/news/${newsId}.png`, { method: 'GET', cache: 'no-store' });
          if (!probeRes.ok) {
            newsImagesFailed.value.add(newsId);
            newsImagesLoaded.value.delete(newsId);
          } else {
            newsImagesLoaded.value.add(newsId);
            newsImagesFailed.value.delete(newsId);
          }
        } catch (probeErr) {
          newsImagesFailed.value.add(newsId);
          newsImagesLoaded.value.delete(newsId);
        }

        proxy.$handleTips(response.data.tag, response.data.msg, error);
      }      

      showAddNewsDialog.value = false;

      // Reset form local state
      addNewsForm.value = {
        mainTitle: '',
        subTitle: '',
        content: '',
        publishDate: getTodayDate(),
        disabled: false
      };
      imagePreview.value = '';
      imageFile.value = null;
      isEditMode.value = false;
      editingNewsId.value = null;
      hasExistingImage.value = false;
      imageChanged.value = false;
      removedImagePending.value = false;
      originalImageUrl.value = '';
      imageHadAtOpen.value = false;    

      // Force reload the list and re-probe images (this will detect the deletion)
      await loadNews();

    } catch (e) {
      const errorMsg = e?.response?.data?.msg || (isEditMode.value ? 'Failed to update news' : 'Failed to add news');
      proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);

    } finally {
      addingNews.value = false;
    }
  });
}

// Preview news in new window
function previewNews(newsItem) {
  const routeData = router.resolve({ 
    name: 'NewsDetail', 
    params: { newsId: newsItem.newsId } 
  });
  window.open(routeData.href, '_blank');
}

function editNews(newsItem) {
  openEditNews(newsItem);
}

async function toggleNewsStatus(newsItem) {
  const action = newsItem.disabled ? 'enable' : 'disable';
  if (!confirm(`Are you sure you want to ${action} news "${newsItem.mainTitle}"?`)) return;
  try {
    const response = await apiClient.post('/Main/switchNewsStatus', {
      newsId: newsItem.newsId,
      disabled: !newsItem.disabled
    });

    if (response && response.data) {
      proxy.$handleTips(response.data.tag, response.data.msg, error);
      newsItem.disabled = !newsItem.disabled;

      // Reload news list
      await loadNews();
    }
  } catch (e) {
    const errorMsg = e?.response?.data?.msg || 'Failed to update news status';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

async function deleteNews(newsItem) {
  if (!confirm(`Are you sure you want to delete news "${newsItem.mainTitle}"?`)) return;
  try {
    const response = await apiClient.post('/Main/removeNews', {
      newsId: newsItem.newsId
    });

    if (response && response.data) {
      proxy.$handleTips(response.data.tag, response.data.msg, error);
    }

    // Reload news list
    await loadNews();
  } catch (e) {
    const errorMsg = e?.response?.data?.msg || 'Failed to delete news';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

// helper to reset dialog-local state (used on cancel / close)
function resetDialogState() {
  // keep global deletedImageIds intact; this only resets temporary dialog flags
  removedImagePending.value = false;
  originalImageUrl.value = '';
  imageHadAtOpen.value = false;  
}

onMounted(async () => {

  // If route contains search, sync local input
  if (route.query.search) searchQuery.value = route.query.search.toString();
  await loadNews();
});
</script>

<style scoped>
.news-management {
  padding: 24px 0;
  color: #e6f0f2;
  min-height: calc(100vh - 84px);
  background: linear-gradient(180deg, #071018 0%, rgba(7,16,24,0.6) 100%);
}

/* header */
.page-title {
  color: #13c2c2;
  font-weight: 700;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.breadcrumb-link {
  color: #13c2c2;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: #0fa6a6;
  text-decoration: underline;
}

.breadcrumb-separator {
  color: rgba(255,255,255,0.4);
  font-weight: 400;
  margin: 0 4px;
}

.breadcrumb-current {
  color: #fff;
  font-weight: 700;
}

.search-input {
  max-width: 300px;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  color: #fff;
}

.search-input::placeholder {
  color: rgba(255,255,255,0.5);
}

.search-input:focus {
  background: rgba(0,0,0,0.35);
  border-color: #13c2c2;
  color: #fff;
  box-shadow: 0 0 0 0.25rem rgba(19,194,194,0.15);
}

.search-input-v2 {
  /* Desktop: enforce a sensible minimum width (260px) and cap */
  width: 260px;
  min-width: 260px;
  max-width: 360px;
}

/* Mobile: make search input full width to match Add button */
@media (max-width: 576px) {
  .search-input-v2 {
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
  }
}

/* news cards - using blue gradient like dashboard news card */
.news-card {
  background: linear-gradient(180deg, rgba(33,150,243,0.15), rgba(21,101,192,0.08));
  border: 1px solid rgba(33,150,243,0.25);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(2,8,12,0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.news-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 40px rgba(2,8,12,0.6);
  border-color: rgba(33,150,243,0.35);
}

.news-title {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.3;
  padding-bottom: 8px;
}

.news-thumbnail {
  width: 140px;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.1);
  flex-shrink: 0;
  background: rgba(0,0,0,0.2);
}

.subtitle-container {
  min-height: 100px;
}

.news-subtitle {
  font-size: 0.875rem;
  line-height: 1.4;
  display: -webkit-box;  
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(255,255,255,0.75) !important;
}

.news-meta {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.7) !important;
}

.news-meta .el-icon {
  color: rgba(255,255,255,0.7) !important;
}

.news-meta span {
  color: rgba(255,255,255,0.7) !important;
}

/* Bootstrap button overrides for theme */
.btn-primary {
  background: #13c2c2;
  border-color: #13c2c2;
  color: #081014;
  font-weight: 600;
}

.btn-primary:hover,
.btn-primary:focus {
  background: #0fa6a6;
  border-color: #0fa6a6;
  color: #081014;
}

.btn-outline-primary {
  color: #13c2c2;
  border-color: rgba(19,194,194,0.3);
}

.btn-outline-primary:hover {
  background: rgba(19,194,194,0.15);
  border-color: #13c2c2;
  color: #13c2c2;
}

.btn-outline-secondary {
  color: rgba(255,255,255,0.7);
  border-color: rgba(255,255,255,0.2);
}

.btn-outline-secondary:hover {
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.3);
  color: #fff;
}

.btn-outline-danger {
  color: #ff8b8b;
  border-color: rgba(255,139,139,0.3);
}

.btn-outline-danger:hover {
  background: rgba(255,139,139,0.15);
  border-color: #ff8b8b;
  color: #ff6b6b;
}

.btn-outline-info {
  color: #5bc0de;
  border-color: rgba(91,192,222,0.3);
}

.btn-outline-info:hover {
  background: rgba(91,192,222,0.15);
  border-color: #5bc0de;
  color: #5bc0de;
}

/* Pagination */
.pagination .page-link {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: #e6fffb;
}

.pagination .page-link:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.15);
  color: #fff;
}

.pagination .page-item.disabled .page-link {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.4);
}

/* loading spinner */
.spinner-border.text-primary {
  color: #13c2c2 !important;
}

.loading-state {
  color: rgba(255,255,255,0.6);
}

/* alerts */
.alert-danger {
  background: rgba(255,139,139,0.15);
  border-color: rgba(255,139,139,0.3);
  color: #ff8b8b;
}

.alert-info {
  background: linear-gradient(180deg, rgba(33,150,243,0.15), rgba(21,101,192,0.08));
  border: 1px solid rgba(33,150,243,0.25);
  color: #bfeff0;
  border-radius: 10px;
}

/* Image upload styles */
.image-upload-item {
  margin-bottom: 20px;
}

.image-collapse {
  width: 100%;
  border: none;
  background: transparent;
}

.image-collapse :deep(.el-collapse-item__header) {
  background: transparent;
  border: none;
  padding: 8px 0;
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  height: auto;
  line-height: 1.5;
  justify-content: flex-start;
}

.image-collapse :deep(.el-collapse-item__header:hover) {
  background: transparent;
  color: rgba(255,255,255,0.9);
}

.image-collapse :deep(.el-collapse-item__wrap) {
  background: transparent;
  border: none;
}

.image-collapse :deep(.el-collapse-item__content) {
  padding: 16px 0 0 0;
  color: #fff;
  display: flex;
  justify-content: center;
}

.image-collapse :deep(.el-collapse-item__arrow) {
  color: rgba(255,255,255,0.6);
  margin-left: 8px;
  order: 1;
}

.collapse-title {
  display: flex;
  align-items: center;
  gap: 8px;
  order: 0;
}

.collapse-title .el-icon {
  font-size: 18px;
  color: rgba(255,255,255,0.6);
  transition: color 0.3s ease;
}

.collapse-title .el-icon:has(svg[class*="picture-filled"]) {
  color: #13c2c2;
}

.news-image-uploader {
  width: 80%;
  margin: 0 auto;
  border: none;
  padding: 0;
  background: transparent;
}

.news-image-uploader :deep(.el-upload) {
  width: 100%;
  display: block;
}

.news-image-uploader.is-disabled :deep(.el-upload) {
  cursor: default !important;
}

.news-image-uploader :deep(.el-upload.is-disabled) {
  cursor: default !important;
}

.upload-placeholder {
  width: 100%;
  max-width: 320px;  
  height: 160px;
  margin: 0 auto;
  border: 2px dashed rgba(255,255,255,0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0,0,0,0.15);
}

.upload-placeholder:hover {
  border-color: #13c2c2;
  background: rgba(19,194,194,0.05);
}

.upload-icon {
  font-size: 48px;
  color: rgba(255,255,255,0.5);
  margin-bottom: 12px;
}

.upload-hint {
  color: rgba(255,255,255,0.5);
  font-size: 12px;
}

.image-preview-container {
  position: relative;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.1);
}

.preview-image {
  width: 100%;
  height: 180px;
  display: block;
  cursor: pointer;
}

.preview-image :deep(.el-image__inner) {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
}

.preview-image :deep(.el-image__wrapper) {
  pointer-events: auto;
}

.preview-image :deep(.el-image__preview) {
  pointer-events: auto;
  z-index: 3001 !important;
}

.preview-image :deep(.el-image-viewer__wrapper) {
  z-index: 9999 !important;
  pointer-events: auto;
}

.preview-image :deep(.el-image-viewer__mask) {
  z-index: 9998 !important;
  pointer-events: auto;
  background-color: rgba(0, 0, 0, 0.8) !important;
}

.preview-image :deep(.el-image-viewer__close) {
  pointer-events: auto !important;
  z-index: 10000 !important;
  color: #fff !important;
}

.preview-image :deep(.el-image-viewer__actions) {
  pointer-events: auto !important;
  z-index: 10000 !important;
}

.preview-image :deep(.el-image-viewer__actions__inner) {
  pointer-events: auto !important;
}

.preview-image :deep(.el-image-viewer__btn) {
  pointer-events: auto !important;
  z-index: 10000 !important;
  color: #fff !important;
}

.preview-image :deep(.el-image-viewer__canvas) {
  pointer-events: auto !important;
  z-index: 9999 !important;
}

.preview-image :deep(.el-image-viewer__img) {
  pointer-events: auto !important;
  z-index: 9999 !important;
}

/* Ensure image is above mask */
.preview-image :deep(.el-image-viewer__canvas img) {
  position: relative !important;
  z-index: 9999 !important;
}

.image-error {
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.3);
  font-size: 48px;
  color: rgba(255,255,255,0.3);
}

.image-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 100;
  pointer-events: auto;
}

.image-actions .el-button {
  opacity: 0.9;
  pointer-events: auto;
}

.image-actions .el-button:hover {
  opacity: 1;
}

/* Hide remove button when image viewer is active */
.image-preview-container:has(.el-image-viewer__wrapper) .image-actions {
  display: none;
}

/* Centered form items for publish date and status */
.centered-form-item {
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-upload-item.centered-form-item {
  display: block;
}

.centered-form-item :deep(.el-form-item__label) {
  width: auto !important;
  text-align: right;
  padding-right: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.centered-form-item :deep(.el-form-item__content) {
  flex: 0 0 auto;
  max-width: 180px;
}

.centered-form-item :deep(.el-date-editor) {
  width: 160px !important;
}

.image-upload-item :deep(.el-form-item__content) {
  max-width: 100%;
  width: 100%;
}

/* responsive adjustments */
@media (max-width: 576px) {
  .page-title {
    font-size: 1.25rem;
    gap: 6px;
  }
  
  .breadcrumb-separator {
    margin: 0 2px;
  }
  
  .search-input {
    max-width: 100%;
  }
  
  .header-actions {
    flex-direction: column;
  }
  
  .header-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .news-thumbnail {
    width: 84px;
    height: 60px;
  }

  .subtitle-container {
    min-height: 60px;
  }

  .centered-form-item {
    display: flex;
    justify-content: center;
    flex-wrap: nowrap;
  }

  .centered-form-item :deep(.el-form-item__label) {
    width: auto !important; 
    text-align: right;
    padding-right: 8px;
    margin-bottom: 0;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .centered-form-item :deep(.el-form-item__content) {
    flex: 0 0 auto;   
    width: 100%;
  }

  .centered-form-item :deep(.el-date-editor) {
    width: 140px !important;
  }

  .upload-placeholder {
    width: 100%;
    max-width: 320px;    
    height: 160px;
    margin: 0 auto;
  }

  .preview-image,
  .image-error {
    width: 100%;
    height: 160px;
  }

  .image-preview-container {
    max-width: 100%;
  }

  .news-image-uploader {
    width: 90%;
    padding: 0;
    border: none;
    background: transparent;
  }

  .image-collapse :deep(.el-collapse-item__content) {
    padding: 16px 0 0 0;
    display: flex;
    justify-content: center;
  }

}
</style>